import { useCallback, useEffect, useState } from 'react';
import { getCompetitionFetch } from 'api/competitions';
import { getEventLookupFetch } from 'api/content';
import { eventFromCompetition } from 'helpers/eventFromCompetition';
import { mergeEvents } from 'helpers/mergeEvents';
import APIErrorsHandlingUtils from 'utils/APIErrorsHandlingUtils';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Один захід за слагом з адреси.
 *
 * Слаг може належати змаганню з e-phan або власному заходу СГОСТУ, тому пошук
 * іде двома кроками (ADR-0012):
 *
 *   1. джерело за слагом. Знайшлося — питаємо CMS доповнення за зовнішнім
 *      ідентифікатором цього змагання;
 *   2. не знайшлося — питаємо CMS уже за самим слагом: це наш власний захід.
 *
 * Збій CMS на другому кроці не ховає змагання: доповнення — це додаток до
 * запису, а не сам запис. Навпаки теж: якщо мовчить джерело, власний захід
 * усе одно відкриється.
 */
export function useEvent(slug) {
  const [state, setState] = useState({ data: null, status: 'loading', error: null });
  const [attempt, setAttempt] = useState(0);
  const language = useLanguage();

  useEffect(() => {
    let cancelled = false;

    if (!slug) {
      setState({ data: null, status: 'failed', error: { kind: 'notFound', status: 404 } });
      return undefined;
    }

    setState((previous) => ({ ...previous, status: 'loading', error: null }));

    (async () => {
      const [sourceError, body] = await getCompetitionFetch(slug);
      const competition = body?.data ? eventFromCompetition(body.data) : null;

      // Ключ, за яким CMS шукає запис: для змагання — його зовнішній
      // ідентифікатор (тобто доповнення), інакше — слаг власного заходу.
      const [, own] = await getEventLookupFetch(competition ? competition.externalId : slug, language);
      const supplement = own || null;

      if (cancelled) return;

      if (competition) {
        const [merged] = mergeEvents([competition], supplement ? [supplement] : []);
        setState({ data: merged || null, status: 'succeeded', error: null });
        return;
      }

      if (supplement) {
        setState({ data: supplement, status: 'succeeded', error: null });
        return;
      }

      // Ніде нічого. Якщо джерело просто мовчало — це збій, який варто
      // повторити; якщо ж воно відповіло «немає» — це звичайна 404.
      const classified = sourceError
        ? APIErrorsHandlingUtils.classify(sourceError)
        : { kind: 'notFound', status: 404 };

      setState({ data: null, status: 'failed', error: classified });
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, language, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, retry };
}
